/**
 * Admin Dashboard tabs rendered as a spinnable wheel — same visual language
 * as the user-facing QuickLinksWheel for brand consistency.
 *
 * Unlike the user wheel which navigates via <Link>, this one fires a
 * callback (`onTabChange`) to switch the active tab in-place.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, useMotionValue, animate, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, BarChart3, Users, MessageSquare, ShoppingCart,
  BookOpen, Dumbbell, Activity, ClipboardList, Apple, Utensils, Eye,
  Zap, ChevronUp, Trophy,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const tabs = [
  { id: 'overview',    Icon: LayoutDashboard, label: 'Overview',   color: 'text-primary',    glow: 'rgba(250,204,21,0.75)', emoji: '📊', desc: 'Stats & summary' },
  { id: 'data',        Icon: BarChart3,       label: 'Data',       color: 'text-cyan-300',   glow: 'rgba(103,232,249,0.75)',emoji: '📈', desc: 'Charts & trends' },
  { id: 'users',       Icon: Users,           label: 'Users',      color: 'text-sky-300',    glow: 'rgba(56,189,248,0.7)',  emoji: '👥', desc: 'Manage members' },
  { id: 'messages',    Icon: MessageSquare,   label: 'Messages',   color: 'text-violet-300', glow: 'rgba(196,181,253,0.75)',emoji: '💬', desc: 'Member inbox' },
  { id: 'purchases',   Icon: ShoppingCart,    label: 'Purchases',  color: 'text-emerald-300',glow: 'rgba(110,231,183,0.75)',emoji: '🛒', desc: 'Sales & orders' },
  { id: 'programs',    Icon: BookOpen,        label: 'Programs',   color: 'text-amber-300',  glow: 'rgba(252,211,77,0.8)',  emoji: '📖', desc: 'Edit programs' },
  { id: 'workouts',    Icon: Dumbbell,        label: 'Workouts',   color: 'text-yellow-300', glow: 'rgba(253,224,71,0.8)',  emoji: '🏋️', desc: 'Edit workouts' },
  { id: 'exercises',   Icon: Activity,        label: 'Exercises',  color: 'text-red-400',    glow: 'rgba(248,113,113,0.75)',emoji: '💪', desc: 'Exercise library' },
  { id: 'user-plans',  Icon: ClipboardList,   label: 'User Plans', color: 'text-blue-300',   glow: 'rgba(147,197,253,0.75)',emoji: '📋', desc: 'AI workout plans' },
  { id: 'nutrition',   Icon: Apple,           label: 'Nutrition',  color: 'text-rose-400',   glow: 'rgba(251,113,133,0.75)',emoji: '🍎', desc: 'Nutrition tips' },
  { id: 'meal-plans',  Icon: Utensils,        label: 'Meal Plans', color: 'text-orange-300', glow: 'rgba(253,186,116,0.75)',emoji: '🍽️', desc: 'AI meal plans' },
  { id: 'challenges',  Icon: Trophy,          label: 'Challenges', color: 'text-yellow-300', glow: 'rgba(253,224,71,0.75)', emoji: '🏆', href: '/challenges', desc: 'Create & edit' },
  { id: 'preview',     Icon: Eye,             label: 'Preview',    color: 'text-purple-300', glow: 'rgba(216,180,254,0.75)',emoji: '👁️', desc: 'See member view' },
];

const ITEMS = tabs.length;
const STEP = 360 / ITEMS;       // 30°
const RADIUS = 95;
const WHEEL_SIZE = 230;

const indexAtTop = (rotationDeg) => {
  const normalized = ((-rotationDeg % 360) + 360) % 360;
  return Math.round(normalized / STEP) % ITEMS;
};

export const AdminTabsWheel = ({ activeTab, onTabChange }) => {
  const initialIdx = Math.max(0, tabs.findIndex((t) => t.id === activeTab));
  const rotation = useMotionValue(-initialIdx * STEP);
  const [focusedIdx, setFocusedIdx] = useState(initialIdx);
  const [expanded, setExpanded] = useState(false);
  const dragOriginRef = useRef({ x: 0, rotation: 0 });
  const navigate = useNavigate();
  // Link-style entries (e.g. Challenges) open their own page instead of a tab.
  const selectTab = (id) => {
    const tab = tabs.find((t) => t.id === id);
    if (tab?.href) navigate(tab.href); else onTabChange(id);
  };

  // Keep the wheel in sync if the active tab changes externally (e.g. stats
  // cards click → setActiveTab from parent).
  useEffect(() => {
    const idx = tabs.findIndex((t) => t.id === activeTab);
    if (idx < 0 || idx === focusedIdx) return;
    const current = rotation.get();
    const target = -idx * STEP;
    const delta = ((target - current + 540) % 360) - 180;
    animate(rotation, current + delta, {
      type: 'spring', stiffness: 220, damping: 26,
      onUpdate: (v) => setFocusedIdx(indexAtTop(v)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const snap = useCallback((finalRotation) => {
    const idx = indexAtTop(finalRotation);
    const target = -idx * STEP;
    const current = rotation.get();
    const delta = ((target - current + 540) % 360) - 180;
    animate(rotation, current + delta, {
      type: 'spring', stiffness: 220, damping: 26,
      onUpdate: (v) => setFocusedIdx(indexAtTop(v)),
    });
  }, [rotation]);

  // Capture the pointer only once a real drag starts so plain clicks reach the icons (desktop).
  const justDraggedRef = useRef(false);
  const onPointerDown = (e) => {
    dragOriginRef.current = { x: e.clientX, rotation: rotation.get(), moved: false, id: e.pointerId };
  };
  const onPointerMove = (e) => {
    if (!e.buttons && e.pointerType === 'mouse') return;
    const o = dragOriginRef.current;
    if (o.x == null) return;
    const dx = e.clientX - o.x;
    if (!o.moved) {
      if (Math.abs(dx) < 6) return;
      o.moved = true;
      e.currentTarget.setPointerCapture?.(o.id);
    }
    const next = o.rotation + dx * 0.6;
    rotation.set(next);
    setFocusedIdx(indexAtTop(next));
  };
  const onPointerUp = () => {
    const o = dragOriginRef.current;
    if (o.moved) {
      justDraggedRef.current = true;
      setTimeout(() => { justDraggedRef.current = false; }, 0);
      snap(rotation.get());
    }
    dragOriginRef.current = { x: null, rotation: rotation.get(), moved: false };
  };

  const spinTo = (i) => {
    const current = rotation.get();
    const target = -i * STEP;
    const delta = ((target - current + 540) % 360) - 180;
    animate(rotation, current + delta, {
      type: 'spring', stiffness: 220, damping: 26,
      onUpdate: (v) => setFocusedIdx(indexAtTop(v)),
    });
  };

  const focused = tabs[focusedIdx];
  const focusedIsActive = focused.id === activeTab;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="mb-6 flex flex-col items-center select-none w-full"
      data-testid="admin-tabs-wheel"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid="admin-tabs-wheel-toggle"
        className="group w-full flex items-center justify-center gap-3 px-5 py-3 rounded-full border border-primary/40 bg-primary/10 hover:bg-primary/20 text-white transition-colors"
        style={{
          textShadow:
            '0 0 4px rgba(255,255,255,0.95), 0 0 10px rgba(255,255,255,0.7), 0 0 18px rgba(212,175,55,0.7)',
        }}
      >
        <Zap className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        <span className="font-heading text-xs uppercase tracking-[0.2em]">
          {expanded ? 'Close panel' : `Section · ${tabs.find((t) => t.id === activeTab)?.label || 'Overview'}`}
        </span>
        <ChevronUp className={`h-3.5 w-3.5 transition-transform ${expanded ? '' : 'rotate-180'}`} />
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center overflow-hidden w-full"
          >
            <div className="mt-3" />
            <div className="relative" style={{ width: WHEEL_SIZE, height: WHEEL_SIZE }}>
              <div className="absolute left-1/2 -translate-x-1/2 -top-2 z-10 pointer-events-none">
                <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[10px] border-t-primary drop-shadow-[0_0_8px_rgba(212,175,55,0.7)]" />
              </div>
              <motion.div
                style={{ width: WHEEL_SIZE, height: WHEEL_SIZE, rotate: rotation }}
                className="absolute left-0 top-0 rounded-full border border-primary/30 bg-gradient-to-b from-primary/10 to-transparent touch-none cursor-grab active:cursor-grabbing"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                data-testid="admin-tabs-wheel-disc"
              >
                {/* Sun-ray ornaments — spokes, ticks, twinkling studs, and a
                    counter-rotating starburst. Matches the QuickLinksWheel
                    look but uses the admin gold palette. */}
                <svg
                  className="absolute inset-0 pointer-events-none"
                  viewBox={`0 0 ${WHEEL_SIZE} ${WHEEL_SIZE}`}
                  aria-hidden="true"
                >
                  {/* Radial spokes — one per tab */}
                  {tabs.map((_, i) => {
                    const a = (i * STEP - 90) * (Math.PI / 180);
                    const x1 = WHEEL_SIZE / 2 + Math.cos(a) * (RADIUS - 48);
                    const y1 = WHEEL_SIZE / 2 + Math.sin(a) * (RADIUS - 48);
                    const x2 = WHEEL_SIZE / 2 + Math.cos(a) * (RADIUS - 10);
                    const y2 = WHEEL_SIZE / 2 + Math.sin(a) * (RADIUS - 10);
                    return (
                      <line
                        key={`a-spoke-${i}`}
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke="rgba(212,175,55,0.25)"
                        strokeWidth="1"
                      />
                    );
                  })}

                  {/* 48 watch-face ticks around the rim — major every 6 */}
                  {Array.from({ length: 48 }).map((_, i) => {
                    const a = (i * 7.5 - 90) * (Math.PI / 180);
                    const isMajor = i % 6 === 0;
                    const isMid = !isMajor && i % 3 === 0;
                    const inner = isMajor ? RADIUS - 4 : RADIUS - 2;
                    const outer = isMajor ? RADIUS + 3 : RADIUS + 1.5;
                    const x1 = WHEEL_SIZE / 2 + Math.cos(a) * inner;
                    const y1 = WHEEL_SIZE / 2 + Math.sin(a) * inner;
                    const x2 = WHEEL_SIZE / 2 + Math.cos(a) * outer;
                    const y2 = WHEEL_SIZE / 2 + Math.sin(a) * outer;
                    return (
                      <line
                        key={`a-tick-${i}`}
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={
                          isMajor ? 'rgba(212,175,55,0.75)'
                          : isMid ? 'rgba(212,175,55,0.4)'
                          : 'rgba(212,175,55,0.22)'
                        }
                        strokeWidth={isMajor ? 1.5 : 0.8}
                      />
                    );
                  })}

                  {/* Twinkling stud dots */}
                  {Array.from({ length: 16 }).map((_, i) => {
                    const a = (i * 22.5 - 90) * (Math.PI / 180);
                    const r = RADIUS - 38;
                    const cx = WHEEL_SIZE / 2 + Math.cos(a) * r;
                    const cy = WHEEL_SIZE / 2 + Math.sin(a) * r;
                    return (
                      <circle
                        key={`a-stud-${i}`}
                        cx={cx} cy={cy} r={1.4}
                        fill="rgba(212,175,55,0.55)"
                        style={{
                          animation: 'wheel-stud-twinkle 3s ease-in-out infinite',
                          animationDelay: `${(i * 0.18).toFixed(2)}s`,
                        }}
                      />
                    );
                  })}

                  {/* Counter-rotating fine spokes between the main ones */}
                  <g className="radar-sweep-reverse" style={{ transformOrigin: `${WHEEL_SIZE / 2}px ${WHEEL_SIZE / 2}px` }}>
                    {Array.from({ length: 16 }).map((_, i) => {
                      const a = (i * 22.5 + 11.25 - 90) * (Math.PI / 180);
                      const x1 = WHEEL_SIZE / 2 + Math.cos(a) * (RADIUS - 48);
                      const y1 = WHEEL_SIZE / 2 + Math.sin(a) * (RADIUS - 48);
                      const x2 = WHEEL_SIZE / 2 + Math.cos(a) * (RADIUS - 30);
                      const y2 = WHEEL_SIZE / 2 + Math.sin(a) * (RADIUS - 30);
                      return (
                        <line
                          key={`a-mini-spoke-${i}`}
                          x1={x1} y1={y1} x2={x2} y2={y2}
                          stroke="rgba(212,175,55,0.22)"
                          strokeWidth="0.8"
                        />
                      );
                    })}
                  </g>
                </svg>

                {/* Slow radar sweep — 11s rotation (spiritual awakening) */}
                <div
                  className="radar-sweep absolute inset-0 rounded-full pointer-events-none mix-blend-screen"
                  aria-hidden="true"
                  style={{
                    background:
                      'conic-gradient(from 0deg, rgba(212,175,55,0) 0deg, rgba(212,175,55,0) 320deg, rgba(212,175,55,0.55) 358deg, rgba(212,175,55,0.85) 360deg, rgba(212,175,55,0) 360deg)',
                    WebkitMaskImage:
                      'radial-gradient(circle, black 35%, black 78%, transparent 80%)',
                    maskImage:
                      'radial-gradient(circle, black 35%, black 78%, transparent 80%)',
                  }}
                />

                {tabs.map(({ Icon, label, id, color, glow, emoji }, i) => {
                  const angle = (i * STEP - 90) * (Math.PI / 180);
                  const x = WHEEL_SIZE / 2 + Math.cos(angle) * RADIUS - 18;
                  const y = WHEEL_SIZE / 2 + Math.sin(angle) * RADIUS - 18;
                  const isFocused = i === focusedIdx;
                  return (
                    <motion.button
                      key={id}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (justDraggedRef.current) return;
                        // Any icon click navigates directly — spin the wheel
                        // for feedback, switch the tab, and close the panel.
                        // Big win on desktop where users expect to click any
                        // icon (not just the highlighted one).
                        if (!isFocused) spinTo(i);
                        selectTab(id);
                        setExpanded(false);
                      }}
                      style={{
                        position: 'absolute', left: x, top: y, width: 36, height: 36,
                        rotate: rotation.get() * -1,
                      }}
                      animate={isFocused ? { scale: [1.1, 1.25, 1.1] } : { scale: 0.92 }}
                      transition={isFocused
                        ? { duration: 1.8, repeat: Infinity, ease: 'easeInOut' }
                        : { type: 'spring', stiffness: 300, damping: 22 }}
                      className={`rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                        isFocused
                          ? 'bg-black/70 border-2 border-primary shadow-[0_0_20px_rgba(212,175,55,0.7)]'
                          : `bg-gradient-to-br from-card to-black/50 border border-primary/30 ${color} hover:border-white/60 hover:brightness-125`
                      }`}
                      data-testid={`admin-tab-wheel-${id}`}
                      aria-label={label}
                    >
                      {isFocused ? (
                        <span
                          className="text-xl leading-none"
                          style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.5))' }}
                          aria-hidden="true"
                        >
                          {emoji}
                        </span>
                      ) : (
                        <Icon
                          className="h-4 w-4"
                          strokeWidth={2.5}
                          style={{ filter: `drop-shadow(0 0 6px ${glow})` }}
                        />
                      )}
                    </motion.button>
                  );
                })}
              </motion.div>

              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5 pointer-events-none">
                <p
                  className="font-heading text-[10px] text-white uppercase tracking-[0.2em] text-center max-w-[110px]"
                  style={{
                    textShadow:
                      '0 0 4px rgba(255,255,255,0.95), 0 0 10px rgba(255,255,255,0.7), 0 0 18px rgba(212,175,55,0.7)',
                  }}
                >
                  {focused.label}
                </p>
                <p className="text-[9px] text-white/60 text-center max-w-[110px] -mt-1" data-testid="admin-tabs-wheel-desc">{focused.desc}</p>
                <button
                  type="button"
                  onClick={() => { if (!focusedIsActive) selectTab(focused.id); setExpanded(false); }}
                  className="pointer-events-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-black font-heading text-[9px] uppercase tracking-wider hover:bg-yellow-300 disabled:opacity-50 transition-colors shadow-lg shadow-primary/30"
                  data-testid="admin-tabs-wheel-go"
                >
                  <span className="text-sm leading-none" aria-hidden="true">{focused.emoji}</span> {focusedIsActive ? "You're here" : 'Open'}
                </button>
              </div>
            </div>

            <p
              className="mt-3 text-[10px] text-white uppercase tracking-wider pb-2 font-medium"
              style={{
                textShadow:
                  '0 0 4px rgba(255,255,255,0.95), 0 0 10px rgba(255,255,255,0.7), 0 0 18px rgba(212,175,55,0.6)',
              }}
            >
              <span className="md:hidden">Swipe the wheel to spin</span>
              <span className="hidden md:inline">Click an icon or drag to spin</span>
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AdminTabsWheel;
